import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalList, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      effective="CREDIKING FINANCIAL SERVICE LIMITED · Last updated: 20th August, 2026"
    >
      <LegalSection title="1. Introduction">
        <p>
          CREDIKING FINANCIAL SERVICE LIMITED is a licensed loan company operating in Nigeria,
          providing personal, business, and other credit facilities. We are committed to protecting
          your privacy and handling your personal data responsibly in accordance with the Nigeria Data
          Protection Act 2023 (NDPA), NDPC guidelines, CBN regulations, and other relevant laws.
        </p>
        <p>
          This Privacy Policy explains how we collect, use, disclose, store, and protect your personal
          data when you interact with us through our website, mobile applications, branches, customer
          service, or other channels. It also outlines your rights as a data subject.
        </p>
        <p>
          By using our services, applying for a loan, or providing your data, you consent to the
          practices described in this Policy. If you do not agree, please do not use our services or
          provide your data.
        </p>
      </LegalSection>

      <LegalSection title="2. Scope">
        <p>
          This Policy applies to all individuals whose personal data we process, including customers,
          loan applicants, guarantors, references, website visitors, and other stakeholders.
        </p>
      </LegalSection>

      <LegalSection title="3. Data We Collect">
        <p className="text-foreground/80 font-medium">Personal identification data</p>
        <LegalList
          items={[
            <>Full name, date of birth, gender, nationality, photograph, and biometric data where permitted.</>,
            <>
              Government-issued identifiers: NIN, BVN, passport, driver’s license, voter’s card, etc.
              (as mandated for KYC and credit services).
            </>,
          ]}
        />
        <p className="text-foreground/80 font-medium">Contact information</p>
        <LegalList items={[<>Residential/business address, email address, phone numbers.</>]} />
        <p className="text-foreground/80 font-medium">Financial and credit information</p>
        <LegalList
          items={[
            <>
              Bank account details, income, employment details, credit history, loan application data,
              transaction history, repayment records, and tax information.
            </>,
            <>Information from credit bureaus or other financial institutions.</>,
          ]}
        />
        <p className="text-foreground/80 font-medium">Sensitive personal data</p>
        <p>
          Processed only with explicit consent, legal obligation, or substantial public interest —
          e.g. health data if relevant to loan assessment, and biometric data for authentication.
        </p>
        <p className="text-foreground/80 font-medium">Technical and usage data</p>
        <LegalList
          items={[
            <>IP address, device information, browser type, location data (with permission), cookies, and analytics data.</>,
          ]}
        />
        <p className="text-foreground/80 font-medium">Other data</p>
        <p>Information from third parties (e.g. employers, referees, credit agencies) or public sources.</p>
      </LegalSection>

      <LegalSection title="4. How We Collect Your Data">
        <LegalList
          items={[
            <>When you apply for a loan, register on our platform, or use our services.</>,
            <>Through website/app interactions, forms, emails, calls, or in-person visits.</>,
            <>From credit bureaus, regulatory bodies, partners, or public databases.</>,
            <>
              Via cookies, web beacons, and similar technologies (see our{" "}
              <Link to="/cookies" className="text-gold hover:underline">
                Cookie Policy
              </Link>
              ).
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Lawful Basis for Processing">
        <LegalList
          items={[
            <>Consent (freely given, informed; you may withdraw anytime).</>,
            <>Contractual necessity (to provide loan services and fulfil agreements).</>,
            <>Legal obligations (CBN, AML/CTF, tax, NDPA, NIN/BVN requirements).</>,
            <>Legitimate interests (fraud prevention, service improvement, security).</>,
            <>Vital interests or public interest where applicable.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Purposes of Processing">
        <LegalList
          items={[
            <>Verify identity, conduct KYC/AML checks, and assess creditworthiness.</>,
            <>Process loan applications, disbursements, repayments, and collections.</>,
            <>Communicate about services, updates, or promotions (with consent where required).</>,
            <>Improve services, conduct research, and analyse usage.</>,
            <>Comply with legal and regulatory requirements.</>,
            <>Prevent and detect fraud, money laundering, or other crimes.</>,
            <>Enforce our rights under loan agreements (including debt recovery).</>,
            <>Respond to inquiries, complaints, or disputes.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Sharing and Disclosure of Your Data">
        <p>We may share your personal data with:</p>
        <LegalList
          items={[
            <>Affiliates and group companies (where applicable).</>,
            <>
              Service providers (credit bureaus, payment processors, cloud services, analytics,
              debt recovery agents) under data processing agreements.
            </>,
            <>Regulators and authorities (CBN, NDPC, NFIU, courts, law enforcement) as required by law.</>,
            <>Credit reference agencies and other financial institutions.</>,
            <>Professional advisers (lawyers, auditors) under confidentiality obligations.</>,
            <>Successors in the event of a merger, acquisition, or business transfer.</>,
          ]}
        />
        <p>We do not sell your personal data.</p>
      </LegalSection>

      <LegalSection title="8. Cross-Border Data Transfers">
        <p>
          If we transfer data outside Nigeria, we ensure appropriate NDPA-compliant safeguards (e.g.
          Standard Contractual Clauses or binding corporate rules).
        </p>
      </LegalSection>

      <LegalSection title="9. Data Security">
        <p>
          We implement reasonable technical, organizational, and administrative measures — including
          encryption, access controls, secure servers, regular audits, and staff training. No system
          is completely secure; you are responsible for keeping your login credentials and devices
          safe.
        </p>
      </LegalSection>

      <LegalSection title="10. Data Retention">
        <p>
          We retain personal data only as long as necessary to fulfil the purposes outlined, comply
          with legal obligations, or resolve disputes. Once no longer needed, we securely delete or
          anonymize it.
        </p>
      </LegalSection>

      <LegalSection title="11. Cookies and Tracking Technologies">
        <p>
          We use cookies and similar technologies to enhance user experience, analyse traffic, and
          personalize content. Manage preferences via our cookie banner or our{" "}
          <Link to="/cookies" className="text-gold hover:underline">
            Cookie Policy
          </Link>
          .
        </p>
      </LegalSection>

      <LegalSection title="12. Your Rights as a Data Subject (under NDPA)">
        <LegalList
          items={[
            <>Access your personal data and request a copy.</>,
            <>Rectification of inaccurate or incomplete data.</>,
            <>Erasure (“right to be forgotten”) where there is no overriding legal basis.</>,
            <>Restriction of processing in certain circumstances.</>,
            <>Objection to processing (e.g. for direct marketing).</>,
            <>Withdraw consent at any time (where processing is based on consent).</>,
          ]}
        />
        <p>To exercise these rights, contact us using the details below. We will respond within one month.</p>
      </LegalSection>

      <LegalSection title="13. Children’s Privacy">
        <p>
          Our services are not directed at children under 18. We do not knowingly collect data from
          minors without verifiable parental/guardian consent where required.
        </p>
      </LegalSection>

      <LegalSection title="14. Changes to This Privacy Policy">
        <p>
          We may update this Policy periodically. Changes will be posted on our website with an
          updated “Last Updated” date. Continued use after changes constitutes acceptance. We will
          notify you of material changes where feasible.
        </p>
      </LegalSection>

      <LegalSection title="15. Contact Us">
        <p>Data Protection Officer / Privacy Team</p>
        <p>Crediking Financial Service Limited</p>
        <p>Address: No 6, University Road, Yaba, Lagos State, Nigeria</p>
        <p>Email: credikingltd@gmail.com</p>
        <p>Phone: 09122290559</p>
      </LegalSection>
    </LegalPage>
  );
}
